/**
 * Creating a norma with its attachment.
 *
 * The Mural uploads first and creates after, because its attachment is part of the notice's own
 * payload. A norma's document is NOT: the upload endpoint is per-norma
 * (`POST /normas/:norma_id/document`), so there is no id to upload against until the norma exists.
 * The order is therefore forced — create, then attach — and what matters is what happens when the
 * second step fails.
 *
 * It must NOT read as "nothing happened": the norma is already there, with its title and text,
 * which is a perfectly valid norma (every row predating migration 227 is exactly that). So the
 * failure is reported as what it is — created, not attached — and the list is refreshed either way
 * so the operator can see the norma and use the per-norma "Anexar" control on it.
 *
 * See normaComposer.test.ts.
 */

export interface NormaDraft {
  title: string;
  content: string;
  order: number;
  file?: File | null;
}

export type NormaCreateOutcome =
  | { kind: 'created' }
  | { kind: 'created-without-attachment'; error: unknown }
  | { kind: 'failed'; error: unknown };

/**
 * A norma needs a title, and then either words or an attachment.
 *
 * Both used to be required, which made publishing a regimento busywork: the document IS the norma,
 * and a paragraph typed to describe it then reads as the norma's text. The Mural settled the same
 * question with "exactly one of Body/PDFURL".
 *
 * The title stays required — a list of untitled entries is unreadable, and it is the only
 * invariant the server can still check (the document arrives in a second request, see
 * createNormaWithAttachment).
 */
export const canCreateNorma = (draft: NormaDraft): boolean => {
  if (draft.title.trim().length === 0) return false;
  return draft.content.trim().length > 0 || !!draft.file;
};

export async function createNormaWithAttachment(
  draft: NormaDraft,
  create: (payload: { title: string; content: string; order: number }) => Promise<{ id: string }>,
  attach: (normaId: string, file: File) => Promise<unknown>,
): Promise<NormaCreateOutcome> {
  let created: { id: string };
  try {
    created = await create({
      title: draft.title.trim(),
      content: draft.content.trim(),
      order: draft.order,
    });
  } catch (error) {
    return { kind: 'failed', error };
  }

  if (!draft.file) return { kind: 'created' };

  try {
    await attach(created.id, draft.file);
    return { kind: 'created' };
  } catch (error) {
    // The norma exists. Saying "erro ao criar norma" here would send the operator back to create
    // it a second time.
    return { kind: 'created-without-attachment', error };
  }
}
